// Touches the WASM bindings only when called, so a module loading it (the bar's
// context) needs nothing more of a test's mock.
import {
  attachment_cell_names,
  DisplayCondition,
  DisplayGroup,
  FilterOperator,
  PhysicalKind,
  is_null_operator,
} from "target_wasm";

/** A categorical cell's value as its categories (it reads `3,5`), a file
 *  cell's as its files' names; any other value whole. */
export const cellValuesOf = (
  kind: PhysicalKind | undefined,
  value: string,
): string[] =>
  kind === PhysicalKind.Categorical
    ? value.split(",")
    : kind === PhysicalKind.Attachment
      ? attachment_cell_names(value)
      : [value];

/** A list value's items, from its JSON (`["a","b"]`); a bare term is one item. */
export const listItems = (value: string): string[] => {
  try {
    const items: unknown = JSON.parse(value);
    if (Array.isArray(items)) return items.map(String);
  } catch {
    // Not the list's JSON: the term as it stands.
  }
  return value ? [value] : [];
};

const sameItems = (a: string[], b: string[]) =>
  a.length === b.length && a.every((item, i) => item === b[i]);

/** A cell's value as a filter names it, part by part (`cellValuesOf`); an
 *  empty cell is one part, null. */
const partsOf = (
  kind: PhysicalKind | undefined,
  value: string | null,
): (string | null)[] => (value === null ? [null] : cellValuesOf(kind, value));

/** Whether a condition names one part of a cell's value: its value is the
 *  part (a list's items in order; as a number where both are one), its list
 *  holds it, or it asks for empty on an empty cell. Naming, not evaluating:
 *  the filter itself runs elsewhere. */
const namesPart = (
  condition: DisplayCondition,
  part: string | null,
): boolean => {
  const { operator } = condition;
  if (operator === undefined) return false;
  if (part === null) return operator === FilterOperator.IsNull;
  if (is_null_operator(operator)) return false;
  // A list names a cell as a whole: the same items, in order.
  if (condition.value_type === PhysicalKind.ListString)
    return (
      condition.value !== undefined &&
      sameItems(listItems(condition.value), listItems(part))
    );
  const same = (stored: string) =>
    stored === part ||
    (stored.trim() !== "" &&
      part.trim() !== "" &&
      Number(stored) === Number(part));
  return (
    (condition.string_array ?? []).some(same) ||
    (condition.value !== undefined && same(condition.value))
  );
};

/** Whether a condition names a cell's value, or any part of it. */
export const namesValue = (
  condition: DisplayCondition,
  value: string | null,
): boolean =>
  partsOf(condition.value_type, value).some((part) =>
    namesPart(condition, part),
  );

/** Every condition of a filter, nested groups' included, in order. */
const conditionsOf = (group: DisplayGroup): DisplayCondition[] =>
  group.items.flatMap((item) => {
    const condition = item.as_condition();
    const nested = item.as_group();
    return condition ? [condition] : nested ? conditionsOf(nested) : [];
  });

/** The parts of a cell's value that no condition on its column names yet:
 *  what filtering by the cell adds. */
export const unnamedParts = (
  group: DisplayGroup,
  columnId: string,
  kind: PhysicalKind | undefined,
  value: string | null,
): (string | null)[] => {
  const conditions = conditionsOf(group).filter((c) => c.field === columnId);
  return partsOf(kind, value).filter(
    (part) => !conditions.some((c) => namesPart(c, part)),
  );
};

/** The ids of what a column's Filter is about, its column's conditions, or a
 *  cell's: those of them naming its value. */
export const conditionsAbout = (
  group: DisplayGroup,
  columnId: string,
  value?: string | null,
): string[] =>
  conditionsOf(group)
    .filter(
      (c) =>
        c.field === columnId && (value === undefined || namesValue(c, value)),
    )
    .map((c) => c.id);
